import { ReactNode, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { ErrorState } from '@/modules/common/components/organisms/error-state';
import { TimeOffTypeCode } from '@/modules/common/types/time-off-type';

import { useTimeOffBalances } from '../../hooks/use-time-off-balances';
import { TimeOffBalancesCompact } from './time-off-balances-compact';
import { TimeOffBalancesSection } from './time-off-balances-section';
import { TimeOffBalancesSkeleton } from './time-off-balances-skeleton';

const HIDDEN_TYPE_CODES: TimeOffTypeCode[] = [TimeOffTypeCode.PUBLIC_HOLIDAY];

interface TimeOffBalancesRenditions {
  /** Title + subtitle of the expanded rendition (fades out on collapse). */
  expandedHeader: ReactNode;
  /** The balance cards of the expanded rendition (scales + fades on collapse). */
  expandedBody: ReactNode;
  /** Compact chip row with tooltips; intended for the collapsed slot. */
  collapsed: ReactNode;
}

/**
 * Builds both the expanded and compact renditions of the user's time-off
 * balances so the page can plug them into a `CollapsibleHeaderSection`. Both
 * renditions share the same data source and visibility filtering, which keeps
 * the two views in sync as the page scrolls.
 */
export const useTimeOffBalancesRenditions = (): TimeOffBalancesRenditions => {
  const { t } = useTranslation('authenticated.timeOff');
  const { data, isLoading, error, refetch, isFetching } = useTimeOffBalances();

  const visibleBalances = useMemo(
    () => (data ?? []).filter(b => !HIDDEN_TYPE_CODES.includes(b.type.code)),
    [data]
  );
  const hasBalances = !isLoading && !error && visibleBalances.length > 0;

  const renderExpandedBody = () => {
    if (isLoading) return <TimeOffBalancesSkeleton />;
    if (error) {
      return (
        <ErrorState
          className="min-h-[180px]"
          title={t('errors.loadBalancesTitle')}
          error={error}
          action={{ label: t('errors.retry'), onClick: () => refetch(), isLoading: isFetching }}
        />
      );
    }
    if (visibleBalances.length === 0) return null;
    return <TimeOffBalancesSection balances={visibleBalances} />;
  };

  const expandedHeader = (
    <div className="flex flex-col gap-1">
      <h2 className="text-lg font-semibold">{t('balances.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('balances.description')}</p>
    </div>
  );

  return {
    expandedHeader,
    expandedBody: renderExpandedBody(),
    // The compact strip only exists once balances are loaded; during
    // loading/error the expanded slot shows the relevant state and there's
    // nothing to collapse into.
    collapsed: hasBalances ? <TimeOffBalancesCompact balances={visibleBalances} /> : null,
  };
};
